import type { ConnectorAccessProbeResult, ConnectorDefinition } from "../types";

// Deliberately duplicated from `decodeJwtPayload` in the Concur capability
// (@workspace/ai). Sharing it would mean importing @workspace/ai from
// @workspace/connectors, and @workspace/ai already depends on this package —
// a cycle. Twelve lines of base64url decode is the cheaper trade.
function decodeJwtSub(token: string): string | undefined {
  const parts = token.split(".");
  if (parts.length !== 3) {
    return undefined;
  }
  try {
    const payload = JSON.parse(
      Buffer.from(parts[1] ?? "", "base64url").toString("utf-8")
    ) as Record<string, unknown>;
    return typeof payload.sub === "string" ? payload.sub : undefined;
  } catch {
    return undefined;
  }
}

function validateConcurAccess(
  token: string
): Promise<ConnectorAccessProbeResult> {
  // Concur's user-JWT APIs are limited — the expense reports v4 list endpoint
  // requires a company JWT and cannot be used as a probe. Instead validate the
  // JWT claims directly: the token was just issued via OAuth so it is fresh.
  const sub = decodeJwtSub(token);
  if (!sub) {
    return Promise.resolve({
      ok: false,
      message:
        "Invalid SAP Concur token — could not read user identity. Try reconnecting.",
    });
  }
  return Promise.resolve({ ok: true });
}

export const concurConnector: ConnectorDefinition = {
  id: "concur",
  name: "SAP Concur",
  description:
    "View and manage expense reports — list reports, view details, create reports, add expense line items, and submit for approval",
  icon: "concur",
  authType: "oauth",
  oauth: {
    // Concur authorize requires www- prefix to avoid browser cert issues.
    // Token exchange for authorization_code grant uses GLZ (global load-balancer),
    // not the datacenter URL — per Concur auth docs.
    authorizationUrl: `${process.env.CONCUR_AUTH_WWW_BASE ?? "https://www-us2.api.concursolutions.com"}/oauth2/v0/authorize`,
    tokenUrl: `${process.env.CONCUR_TOKEN_URL ?? "https://glz.api.concursolutions.com/oauth2/v0/token"}`,
    scopes: [
      "openid",
      "user.read",
      "EXPRPT",
      "expense.report.read",
      "expense.report.readwrite",
      "quickexpense.writeonly",
      // Image v1 API — upload receipt images on behalf of the connected user
      // (Available Receipts pool or a specific expense entry). No company JWT.
      "IMAGE",
      "attendee.read",
      "receipts.read",
      "expense.config.expensetypes.restricted.read",
      "expense.config.policies.restricted.read",
      // Spend lists (read-only): resolve B&G coding codes — Job Number /
      // Overhead, Cost Type / Object Account, Cost Code / Subsidiary — to the
      // list items the expense form's OrgUnit fields require.
      "spend.list.read",
      "spend.listitem.read",
      // Trips v1.1 (legacy, user-level): read the user's Concur Travel air
      // bookings to recover ticket numbers (PlatingCarrierNumericCode +
      // PlatingControlNumber) — the expense APIs can't write TicketNumber, so
      // Magnus looks it up for the user to enter.
      "ITINER",
      // Concur rejects the whole authorize flow when an app requests a scope
      // its App Center registration doesn't hold, so confirm a new scope is
      // granted (the token's `concur.scopes` claim lists what was) before
      // shipping it.
    ],
    // User-level token app registration. The company-level token flow uses a
    // separate registration and env vars: see
    // ./concur-company.ts (ADR-132).
    clientIdEnvVar: "CONCUR_USER_LEVEL_TOKEN_CLIENT_ID",
    clientSecretEnvVar: "CONCUR_USER_LEVEL_TOKEN_CLIENT_SECRET",
  },
  isEnabled: () =>
    Boolean(
      process.env.CONCUR_USER_LEVEL_TOKEN_CLIENT_ID &&
        process.env.CONCUR_USER_LEVEL_TOKEN_CLIENT_SECRET
    ),
  validateAccess: validateConcurAccess,
};
